/**
 * Platform Storage Abstraction
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';

export interface StoragePlatform {
  // Secure storage (tokens, sensitive data)
  getSecure(key: string): Promise<string | null>;
  setSecure(key: string, value: string): Promise<void>;
  deleteSecure(key: string): Promise<void>;

  // Regular storage (preferences)
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

class WebStorage implements StoragePlatform {
  async getSecure(key: string): Promise<string | null> {
    // Web doesn't have truly secure storage, use localStorage with prefix
    return localStorage.getItem(`secure_${key}`);
  }

  async setSecure(key: string, value: string): Promise<void> {
    localStorage.setItem(`secure_${key}`, value);
  }

  async deleteSecure(key: string): Promise<void> {
    localStorage.removeItem(`secure_${key}`);
  }

  async get(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  }

  async set(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  }

  async delete(key: string): Promise<void> {
    localStorage.removeItem(key);
  }
}

class NativeStorage implements StoragePlatform {
  async getSecure(key: string): Promise<string | null> {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.STORAGE,
        BridgeActions.GET_SECURE,
        { key }
      );
    } catch (e) {
      console.error('[NativeStorage] getSecure error:', e);
      return null;
    }
  }

  async setSecure(key: string, value: string): Promise<void> {
    try {
      await bridge.request(BridgeChannels.STORAGE, BridgeActions.SET_SECURE, { key, value });
    } catch (e) {
      console.error('[NativeStorage] setSecure error:', e);
    }
  }

  async deleteSecure(key: string): Promise<void> {
    try {
      await bridge.request(BridgeChannels.STORAGE, BridgeActions.DELETE_SECURE, { key });
    } catch (e) {
      console.error('[NativeStorage] deleteSecure error:', e);
    }
  }

  async get(key: string): Promise<string | null> {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.STORAGE,
        BridgeActions.GET,
        { key }
      );
    } catch (e) {
      console.error('[NativeStorage] get error:', e);
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      await bridge.request(BridgeChannels.STORAGE, BridgeActions.SET, { key, value });
    } catch (e) {
      console.error('[NativeStorage] set error:', e);
    }
  }

  async delete(key: string): Promise<void> {
    try {
      await bridge.request(BridgeChannels.STORAGE, BridgeActions.DELETE, { key });
    } catch (e) {
      console.error('[NativeStorage] delete error:', e);
    }
  }
}

const webStorage = new WebStorage();
const nativeStorage = new NativeStorage();

export function getStoragePlatform(): StoragePlatform {
  return bridge.isNative ? nativeStorage : webStorage;
}

